import React, { createContext, useEffect, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAuth } from './AuthContext'

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
  connectionError: string | null
}

export const SocketContext = createContext<SocketContextType | undefined>(undefined)

interface SocketProviderProps {
  children: React.ReactNode
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const { isAuthenticated, currentAgent } = useAuth()

  useEffect(() => {
    if (!isAuthenticated || !currentAgent) {
      return
    }

    // Socket endpoint resolution:
    //   1. VITE_SOCKET_URL if explicitly set (any environment).
    //   2. Dev: localhost:3001 — the dev-server's API/socket port (vite serves
    //      the dashboard on 5174 and only proxies REST over to 3001, not ws).
    //   3. Prod: same-origin (window.location.origin). Served from
    //      https://wetask.cloud, the nginx proxy upgrades /socket.io to the
    //      api container. A hardcoded localhost here would make the deployed
    //      dashboard try to open its websocket to the agent's OWN machine —
    //      which is exactly the bug this avoids. (Matches widget useSocket.)
    const socketUrl =
      import.meta.env.VITE_SOCKET_URL ||
      (import.meta.env.DEV ? 'http://localhost:3001' : window.location.origin)

    // SEC-01: Pass JWT token for Socket.IO auth middleware (falls back to agentId for compat).
    //
    // The token MUST be in `query` as well as `auth`. The API's websocket
    // service reads `socket.handshake.query.token` (see websocket.service.ts
    // handleConnection ~line 289). Without the query copy, the server sees
    // no token, calls socket.disconnect(true), and every subsequent emit
    // POSTs to a now-dead session with a 400 Bad Request. The widget already
    // does this correctly — the dashboard was the odd one out.
    const token = localStorage.getItem('auth_token')
    const newSocket = io(socketUrl, {
      auth: {
        token,
        agentId: currentAgent.id
      },
      query: {
        token: token || '',
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // Never permanently give up. Previously this was 10 — after ~10 failed
      // retries (a longer network blip, laptop sleep/wake, or every API restart
      // on deploy) socket.io fired `reconnect_failed` and STOPPED trying, so no
      // live message arrived until the agent manually refreshed. Retrying
      // forever (with the capped backoff above) is what keeps the thread live.
      reconnectionAttempts: Infinity
    })

    newSocket.on('connect', () => {
      setIsConnected(true)
      setConnectionError(null)

      // Join agent and queue rooms (use event names the server expects)
      newSocket.emit('join_agent', currentAgent.id)
      newSocket.emit('join_queue')
    })

    newSocket.on('disconnect', () => {
      setIsConnected(false)
    })

    newSocket.on('connect_error', (error) => {
      setConnectionError(error.message || 'Connection error')
    })

    newSocket.on('reconnect_failed', () => {
      setConnectionError('Connection lost. Please refresh the page to reconnect.')
      setIsConnected(false)
    })

    newSocket.on('reconnect', () => {
      setConnectionError(null)
      setIsConnected(true)
    })

    setSocket(newSocket)

    return () => {
      newSocket.disconnect()
    }
  }, [isAuthenticated, currentAgent])

  const value: SocketContextType = {
    socket,
    isConnected,
    connectionError
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}

export const useSocket = () => {
  const context = React.useContext(SocketContext)
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}
